import { useId, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Plus, X } from 'lucide-react';
import { Dialog, DialogCancel } from '@/components/ui/Dialog';
import { Button } from '@/components/ui/Button';
import { Field } from '@/components/ui/Field';
import { ApiErrorAlert } from '@/components/ui/ApiErrorAlert';
import { listDeviceTags, updateDeviceTags } from '@/lib/api/endpoints/devices';
import { queryKeys } from '@/lib/query-keys';
import { isApiError } from '@/lib/api/errors';
import type { Device } from '@/lib/api/types';

/*
  Edit the tag set on a device.

  Tags are a machine value, not prose: they are matched exactly by the device list filter and
  by policy rules, so "PCI" and "pci" being two different tags would be a quiet mess. The
  input therefore lower-cases and trims as it commits, and the pattern is checked locally
  before the request so a typo does not cost a round trip. The same pattern is enforced by the
  API, and the API's answer is what counts.

  Keyboard behaviour, which is most of the work in a chip editor:

  * Enter in the text field adds the tag. The field lives in a small form of its own, so Enter
    does not submit the dialog and lose the half-typed tag. The dialog's own save is the
    footer button, reachable by Tab.
  * Comma also commits, because pasting "front-desk, pci" is what people actually do.
  * Backspace in an empty field removes the last tag, which is the convention every chip input
    has and the one people reach for without thinking.
  * Each chip's remove control is a real button with an accessible name that includes the tag,
    so a screen reader user hears "Remove tag pci" rather than four identical "Remove"s.
*/

const TAG_PATTERN = /^[a-z0-9][a-z0-9-]{0,39}$/;
const MAX_TAGS = 20;

export interface ChangeTagsDialogProps {
  readonly open: boolean;
  readonly onOpenChange: (open: boolean) => void;
  readonly device: Device;
}

export function ChangeTagsDialog({
  open,
  onOpenChange,
  device,
}: ChangeTagsDialogProps): React.JSX.Element {
  const tagFormId = `tag-add-${useId()}`;
  const queryClient = useQueryClient();

  const [tags, setTags] = useState<readonly string[]>(device.tags);
  const [draft, setDraft] = useState('');
  const [localError, setLocalError] = useState<string | undefined>(undefined);

  const suggestions = useQuery({
    queryKey: queryKeys.deviceTags,
    queryFn: listDeviceTags,
    staleTime: 5 * 60_000,
  });

  // Reopening starts from the tags currently on the device, not from a cancelled edit.
  // Adjusted during render rather than in an effect, for the reason spelled out in
  // ConfirmDialog: an effect shows the stale set for a frame first.
  const [lastOpen, setLastOpen] = useState(open);
  if (open !== lastOpen) {
    setLastOpen(open);
    if (open) {
      setTags(device.tags);
      setDraft('');
      setLocalError(undefined);
    }
  }

  const mutation = useMutation({
    mutationFn: (next: readonly string[]) => updateDeviceTags(device.id, { tags: next }),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['devices'] });
      onOpenChange(false);
    },
  });

  /**
   * Commits one tag or a comma separated run of them.
   *
   * It has to handle several at once rather than being called in a loop: calling a
   * single-tag version repeatedly inside one event handler would read `tags` from this
   * render every time and each call would overwrite the last, so pasting three tags would
   * add one. Everything is accumulated into one array and committed once.
   */
  function commit(raw: string): void {
    const candidates = raw
      .split(',')
      .map((part) => part.trim().toLowerCase())
      .filter((part) => part !== '');
    if (candidates.length === 0) {
      return;
    }

    const next = [...tags];
    let error: string | undefined;
    for (const value of candidates) {
      if (!TAG_PATTERN.test(value)) {
        error =
          'Lower case letters, digits and hyphens only, starting with a letter or digit, up to 40 characters.';
        continue;
      }
      if (next.includes(value)) {
        error = `${value} is already on this device.`;
        continue;
      }
      if (next.length >= MAX_TAGS) {
        error = `A device can carry at most ${String(MAX_TAGS)} tags.`;
        break;
      }
      next.push(value);
    }

    setTags(next.sort((a, b) => a.localeCompare(b)));
    setLocalError(error);
    if (error === undefined) {
      setDraft('');
    }
  }

  function remove(tag: string): void {
    setTags(tags.filter((candidate) => candidate !== tag));
    setLocalError(undefined);
  }

  const unchanged =
    tags.length === device.tags.length && tags.every((tag) => device.tags.includes(tag));
  const serverFieldError = isApiError(mutation.error) ? mutation.error.fieldErrors.tags?.[0] : undefined;
  const unused = (suggestions.data ?? []).filter((tag) => !tags.includes(tag));

  return (
    <Dialog
      open={open}
      onOpenChange={onOpenChange}
      title={`Tags on ${device.hostname}`}
      description="Tags drive device list filters and policy targeting, so they are matched exactly and always lower case."
      busy={mutation.isPending}
      footer={
        <>
          <DialogCancel disabled={mutation.isPending} />
          <Button
            variant="primary"
            loading={mutation.isPending}
            disabled={unchanged}
            onClick={() => {
              mutation.mutate(tags);
            }}
          >
            Save tags
          </Button>
        </>
      }
    >
      <div className="flex flex-col gap-4">
        <div>
          <h3 className="mb-2 text-xs font-semibold tracking-wide text-fg-muted uppercase">
            On this device ({tags.length})
          </h3>
          {tags.length === 0 ? (
            <p className="text-sm text-fg-subtle italic">No tags.</p>
          ) : (
            <ul className="flex flex-wrap gap-1.5">
              {tags.map((tag) => (
                <li key={tag}>
                  <span className="inline-flex items-center gap-1 rounded border border-border bg-surface-sunken py-0.5 pr-0.5 pl-1.5 font-mono text-[12px] text-fg-muted">
                    {tag}
                    <button
                      type="button"
                      aria-label={`Remove tag ${tag}`}
                      onClick={() => {
                        remove(tag);
                      }}
                      className="rounded p-0.5 text-fg-subtle hover:bg-surface-hover hover:text-[var(--color-danger)]"
                    >
                      <X className="size-3" aria-hidden="true" />
                    </button>
                  </span>
                </li>
              ))}
            </ul>
          )}
        </div>

        {/*
          A nested form is not valid HTML, so this is a sibling form and the dialog's save is
          a plain button rather than a submit. That keeps Enter in the tag box meaning "add
          this tag" instead of "save and close with the tag discarded".
        */}
        <form
          id={tagFormId}
          onSubmit={(event) => {
            event.preventDefault();
            commit(draft);
          }}
          className="flex items-end gap-2"
        >
          <Field
            label="Add a tag"
            hint="Press Enter to add. Backspace in an empty box removes the last tag."
            value={draft}
            maxLength={40}
            autoComplete="off"
            className="font-mono"
            onChange={(event) => {
              const raw = event.target.value;
              if (raw.includes(',')) {
                // A pasted comma separated list commits everything before the last comma and
                // leaves the trailing fragment in the box to keep typing.
                const parts = raw.split(',');
                const last = parts.pop() ?? '';
                commit(parts.join(','));
                setDraft(last.trim().toLowerCase());
                return;
              }
              setDraft(raw.toLowerCase());
              setLocalError(undefined);
            }}
            onKeyDown={(event) => {
              if (event.key === 'Backspace' && draft === '' && tags.length > 0) {
                event.preventDefault();
                const last = tags[tags.length - 1];
                if (last !== undefined) {
                  remove(last);
                }
              }
            }}
            {...(localError === undefined ? {} : { error: localError })}
          />
          <Button type="submit" variant="secondary" className="mb-6" disabled={draft.trim() === ''}>
            <Plus className="size-4" aria-hidden="true" />
            Add
          </Button>
        </form>

        {unused.length > 0 ? (
          <div>
            <h3 className="mb-2 text-xs font-semibold tracking-wide text-fg-muted uppercase">
              Used elsewhere in the fleet
            </h3>
            <ul className="flex flex-wrap gap-1.5">
              {unused.slice(0, 24).map((tag) => (
                <li key={tag}>
                  <button
                    type="button"
                    onClick={() => {
                      commit(tag);
                    }}
                    className="inline-flex items-center gap-1 rounded border border-dashed border-border px-1.5 py-0.5 font-mono text-[12px] text-fg-subtle hover:border-accent-line hover:text-accent"
                  >
                    <Plus className="size-3" aria-hidden="true" />
                    {tag}
                  </button>
                </li>
              ))}
            </ul>
          </div>
        ) : null}

        {serverFieldError !== undefined ? (
          <p role="alert" className="text-xs font-medium text-[var(--color-danger)]">
            {serverFieldError}
          </p>
        ) : mutation.isError ? (
          <ApiErrorAlert error={mutation.error} context="Could not save the tags" />
        ) : null}
      </div>
    </Dialog>
  );
}
